import React, { useEffect, useRef, useState } from "react";
import { Modal } from "../../../../../components/common";
import { MapPin, Navigation, Info, Camera, Image as ImageIcon, Clock, X } from "lucide-react";
import api from "../../../../../services/api";

export const AttendanceMapModal = ({ isOpen, onClose, attendance }) => {
    const mapContainerRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const [leafletLoaded, setLeafletLoaded] = useState(false);
    const [activeTab, setActiveTab] = useState("tracking"); // "tracking" | "updates"
    const [workUpdates, setWorkUpdates] = useState([]);
    const [loadingUpdates, setLoadingUpdates] = useState(false);
    
    const [expenses, setExpenses] = useState([]);
    const [loadingExpenses, setLoadingExpenses] = useState(false);

    const [selectedImage, setSelectedImage] = useState(null);

    // 1. Inject Leaflet CDN dynamically
    useEffect(() => {
        if (!isOpen) return;

        const linkId = "leaflet-css-cdn";
        const scriptId = "leaflet-js-cdn";

        if (!document.getElementById(linkId)) {
            const link = document.createElement("link");
            link.id = linkId;
            link.rel = "stylesheet";
            link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
            document.head.appendChild(link);
        }

        if (!document.getElementById(scriptId)) {
            const script = document.createElement("script");
            script.id = scriptId;
            script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
            script.onload = () => setLeafletLoaded(true);
            document.head.appendChild(script);
        } else if (window.L) {
            setLeafletLoaded(true);
        }
    }, [isOpen]);

    // 2. Fetch Work Updates
    useEffect(() => {
        if (isOpen && attendance?._id) {
            const fetchUpdates = async () => {
                try {
                    setLoadingUpdates(true);
                    const res = await api.get(`/attendance/work-updates/all?attendanceId=${attendance._id}`);
                    if (res && res.data && Array.isArray(res.data.updates)) {
                        setWorkUpdates(res.data.updates);
                    }
                } catch (err) {
                    console.error("Failed to fetch updates", err);
                } finally {
                    setLoadingUpdates(false);
                }
            };
            
            const fetchExpenses = async () => {
                try {
                    setLoadingExpenses(true);
                    const res = await api.get(`/attendance/expense/all?attendanceId=${attendance._id}`);
                    if (res?.data?.expenses) {
                        setExpenses(res.data.expenses);
                    }
                } catch (err) {
                    console.error("Failed to fetch expenses", err);
                } finally {
                    setLoadingExpenses(false);
                }
            };

            fetchUpdates();
            fetchExpenses();
        }
    }, [isOpen, attendance]);

    // 3. Extract and format coordinates
    const getMapData = () => {
        if (!attendance) return [];
        const data = [];

        if (attendance.checkInLocation?.lat && attendance.checkInLocation?.lng) {
            data.push({
                lat: attendance.checkInLocation.lat,
                lng: attendance.checkInLocation.lng,
                type: "checkin",
                label: "Check-In",
                color: "#1E3A8A",
                time: attendance.checkIn
            });
        }

        attendance.breaks?.forEach((b, idx) => {
            if (b.startLocation?.lat && b.startLocation?.lng) {
                data.push({
                    lat: b.startLocation.lat,
                    lng: b.startLocation.lng,
                    type: "break_start",
                    label: `Break ${idx + 1} Start`,
                    color: "#F97316",
                    time: b.start
                });
            }
            if (b.endLocation?.lat && b.endLocation?.lng) {
                data.push({
                    lat: b.endLocation.lat,
                    lng: b.endLocation.lng,
                    type: "break_end",
                    label: `Break ${idx + 1} End`,
                    color: "#10B981",
                    time: b.end
                });
            }
        });

        if (attendance.checkOutLocation?.lat && attendance.checkOutLocation?.lng) {
            data.push({
                lat: attendance.checkOutLocation.lat,
                lng: attendance.checkOutLocation.lng,
                type: "checkout",
                label: "Check-Out",
                color: "#EF4444",
                time: attendance.checkOut
            });
        }

        // Add Background Tracking Points
        attendance.trackingPath?.forEach((p) => {
            data.push({
                lat: p.lat,
                lng: p.lng,
                type: "tracking",
                label: "Auto-Ping",
                color: "#94A3B8",
                time: p.timestamp
            });
        });

        return data.sort((a, b) => new Date(a.time) - new Date(b.time));
    };

    // 4. Initialize Map
    useEffect(() => {
        if (!leafletLoaded || !isOpen || !mapContainerRef.current || activeTab !== "tracking") return;

        const L = window.L;
        const mapData = getMapData();

        // Cleanup existing map
        if (mapInstanceRef.current) {
            mapInstanceRef.current.remove();
        }

        // Initialize Map
        const map = L.map(mapContainerRef.current, {
            zoomControl: false,
            scrollWheelZoom: true
        });
        mapInstanceRef.current = map;

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; OpenStreetMap'
        }).addTo(map);

        L.control.zoom({ position: 'bottomright' }).addTo(map);

        const allPoints = [];

        // Add path markers (Check-in, Tracking, etc)
        mapData.forEach(d => {
            allPoints.push([d.lat, d.lng]);
            const isTracking = d.type === "tracking";
            const size = isTracking ? 8 : 14;
            
            const markerHtml = `
                <div style="background-color: ${d.color}; width: ${size}px; height: ${size}px; border: ${isTracking ? '1.5px' : '3px'} solid white; border-radius: 50%; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>
            `;
            const icon = L.divIcon({ className: 'custom-div-icon', html: markerHtml, iconSize: [size, size], iconAnchor: [size/2, size/2] });
            const marker = L.marker([d.lat, d.lng], { icon }).addTo(map);
            marker.bindPopup(`<div style="font-family: 'Inter', sans-serif; font-size: 11px;"><b style="color: ${d.color}">${d.label}</b><br/>${new Date(d.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>`);
        });

        // Add Work Update markers to the tracking map
        workUpdates.forEach(u => {
            if (u.location?.lat && u.location?.lng) {
                allPoints.push([u.location.lat, u.location.lng]);
                const markerHtml = `
                    <div style="background-color: #7C3AED; width: 22px; height: 22px; border: 3px solid white; border-radius: 6px; box-shadow: 0 2px 4px rgba(0,0,0,0.2); display: flex; align-items: center; justify-content: center; color: white;">
                         <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/><circle cx="12" cy="13" r="3"/></svg>
                        </div>
                `;
                const icon = L.divIcon({ className: 'update-icon', html: markerHtml, iconSize: [22, 22], iconAnchor: [11, 11] });
                const marker = L.marker([u.location.lat, u.location.lng], { icon }).addTo(map);
                
                const imagesHtml = u.images?.map(img => `<img src="${import.meta.env.VITE_API_URL || 'http://localhost:5000'}${img}" style="width: 50px; height: 50px; object-fit: cover; border-radius: 4px;" />`).join('') || '';
                
                marker.bindPopup(`
                    <div style="font-family: 'Inter', sans-serif; width: 180px;">
                        <b style="color: #7C3AED; font-size: 12px; display: block; margin-bottom: 2px;">${u.title}</b>
                        <div style="font-size: 10px; color: #64748B; margin: 4px 0; line-height: 1.4;">${u.description || 'Field update'}</div>
                        <div style="display: flex; gap: 4px; flex-wrap: wrap; margin-top: 6px;">${imagesHtml}</div>
                    </div>
                `);
            }
        });

        const pathPoints = mapData.map(d => [d.lat, d.lng]);
        if (pathPoints.length > 1) {
            L.polyline(pathPoints, { color: '#64748b', weight: 4, opacity: 0.6, dashArray: '8, 12' }).addTo(map);
        }

        if (allPoints.length > 0) {
            map.fitBounds(L.latLngBounds(allPoints), { padding: [50, 50] });
        } else {
            map.setView([20.5937, 78.9629], 5);
        }

        return () => {
            if (mapInstanceRef.current) {
                mapInstanceRef.current.remove();
                mapInstanceRef.current = null;
            }
        };
    }, [leafletLoaded, isOpen, attendance, activeTab, workUpdates]);

    const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

    return (
        <>
        <Modal isOpen={isOpen} onClose={onClose} title={`Tracking: ${attendance?.user?.name}`} size="max-w-5xl">
            <div className="flex flex-col h-[650px]">
                {/* Tabs Selector */}
                <div className="flex items-center gap-10 border-b border-slate-100 mb-6 flex-shrink-0 px-2">
                    <button 
                        onClick={() => setActiveTab("tracking")}
                        className={`pb-3 px-1 text-[13px] font-bold transition-all flex items-center gap-2 border-b-2 ${activeTab === "tracking" ? "border-brand-blue text-brand-blue" : "border-transparent text-slate-400 hover:text-slate-500"}`}
                    >
                        <Navigation size={18} /> Path Tracking
                    </button>
                    <button 
                        onClick={() => setActiveTab("updates")}
                        className={`pb-3 px-1 text-[13px] font-bold transition-all flex items-center gap-2 border-b-2 ${activeTab === "updates" ? "border-brand-blue text-brand-blue" : "border-transparent text-slate-400 hover:text-slate-500"}`}
                    >
                        <Camera size={18} /> Work Updates ({workUpdates.length})
                    </button>
                    <button 
                        onClick={() => setActiveTab("expenses")}
                        className={`pb-3 px-1 text-[13px] font-bold transition-all flex items-center gap-2 border-b-2 ${activeTab === "expenses" ? "border-brand-blue text-brand-blue" : "border-transparent text-slate-400 hover:text-slate-500"}`}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="12" cy="12" r="2"/><path d="M6 12h.01M18 12h.01"/></svg>
                        Expenses ({expenses.length})
                    </button>
                </div>

                {activeTab === "tracking" ? (
                    <>
                        <div className="grid grid-cols-4 gap-8 mb-6 flex-shrink-0 px-2">
                            <div className="flex flex-col">
                                <span className="text-[10px] uppercase font-bold text-slate-400 mb-0.5 tracking-widest">Net Work</span>
                                <span className="text-[15px] font-bold text-brand-dark tabular-nums">{attendance?.totalHours || '0.00'} hr</span>
                            </div>
                            <div className="flex flex-col">
                                <span className="text-[10px] uppercase font-bold text-slate-400 mb-0.5 tracking-widest">Total Break</span>
                                <span className="text-[15px] font-bold text-brand-dark tabular-nums">{attendance?.totalBreakMinutes || 0} min</span>
                            </div>
                            <div className="flex flex-col">
                                <span className="text-[10px] uppercase font-bold text-slate-400 mb-0.5 tracking-widest">Field Reports</span>
                                <span className="text-[15px] font-bold text-brand-dark tabular-nums">{workUpdates.length} updates</span>
                            </div>
                            <div className="flex flex-col">
                                <span className="text-[10px] uppercase font-bold text-slate-400 mb-0.5 tracking-widest">Check-In</span>
                                <span className="text-[15px] font-bold text-brand-dark tabular-nums">{attendance?.checkIn ? new Date(attendance.checkIn).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '--'}</span>
                            </div>
                        </div>

                        <div className="relative flex-1 rounded-3xl overflow-hidden border border-slate-100 bg-slate-50 shadow-sm">
                            {!leafletLoaded && (
                                <div className="absolute inset-0 flex items-center justify-center z-10 bg-slate-50/80 backdrop-blur-sm">
                                    <div className="flex flex-col items-center gap-3">
                                        <div className="w-8 h-8 border-4 border-brand-blue border-t-transparent rounded-full animate-spin" />
                                        <div className="text-sm font-semibold text-brand-dark">Initializing Tracking View...</div>
                                    </div>
                                </div>
                            )}
                            <div ref={mapContainerRef} className="h-full w-full" />
                            <div className="absolute top-4 right-4 z-[1000] bg-white px-4 py-2.5 rounded-2xl shadow-xl border border-slate-100">
                                <div className="text-[11px] font-bold text-slate-600 flex items-center gap-2.5">
                                    <div className="w-2.5 h-2.5 rounded-full bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.5)] animate-pulse" />
                                    Live Path + Work Pins
                                </div>
                            </div>
                        </div>
                    </>
                ) : activeTab === "updates" ? (
                    <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar pb-6">
                        {workUpdates.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full py-20 text-center">
                                <div className="w-16 h-16 bg-slate-50 rounded-3xl flex items-center justify-center mb-4">
                                    <Camera className="text-slate-300" size={32} />
                                </div>
                                <h3 className="text-lg font-bold text-brand-dark">No Updates Yet</h3>
                                <p className="text-sm text-slate-400 max-w-xs mt-1">This shift doesn't have any multimedia work updates submitted.</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 gap-6">
                                {workUpdates.map((update) => (
                                    <div key={update._id} className="bg-white border border-slate-100 rounded-[24px] overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                                        <div className="p-5">
                                            <div className="flex items-center justify-between mb-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
                                                        <Camera size={20} className="text-purple-600" />
                                                    </div>
                                                    <div>
                                                        <h4 className="font-bold text-brand-dark text-base">{update.title}</h4>
                                                        <div className="flex items-center gap-2 mt-0.5">
                                                            <Clock size={12} className="text-slate-400" />
                                                            <span className="text-[11px] text-slate-400 font-medium">{new Date(update.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                                {update.location && (
                                                    <div className="flex items-center gap-1.5 bg-slate-50 px-3 py-1.5 rounded-full">
                                                        <MapPin size={12} className="text-slate-400" />
                                                        <span className="text-[10px] font-bold text-slate-500">GPS Attached</span>
                                                    </div>
                                                )}
                                            </div>
                                            {update.description && <p className="text-sm text-slate-600 leading-relaxed mb-4">{update.description}</p>}
                                            {update.images && update.images.length > 0 && (
                                                <div className="flex flex-wrap gap-3">
                                                    {update.images.map((img, idx) => (
                                                        <div key={idx} className="group relative cursor-pointer" onClick={() => setSelectedImage(img)}>
                                                            <img src={`${API_URL}${img}`} className="w-24 h-24 object-cover rounded-2xl border border-slate-100 shadow-sm" alt="Work" />
                                                            <div className="absolute inset-0 bg-black/5 opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl" />
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ) : activeTab === "expenses" ? (
                    <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar pb-6">
                        {expenses.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full py-20 text-center">
                                <div className="w-16 h-16 bg-slate-50 rounded-3xl flex items-center justify-center mb-4">
                                    <svg className="text-slate-300" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="12" cy="12" r="2"/><path d="M6 12h.01M18 12h.01"/></svg>
                                </div>
                                <h3 className="text-lg font-bold text-brand-dark">No Expenses Logged</h3>
                                <p className="text-sm text-slate-400 max-w-xs mt-1">This shift has zero expense claims submitted in the field.</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 gap-6">
                                {expenses.map((exp) => (
                                    <div key={exp._id} className="bg-white border border-slate-100 rounded-[24px] overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                                        <div className="p-5 flex gap-6">
                                            <div className="flex-1">
                                                <div className="flex items-center justify-between mb-4">
                                                    <div>
                                                        <h4 className="font-bold text-brand-dark text-[18px]">{exp.title}</h4>
                                                        <div className="flex items-center gap-2 mt-1">
                                                            <Clock size={12} className="text-slate-400" />
                                                            <span className="text-[12px] text-slate-400 font-medium">{new Date(exp.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                                        </div>
                                                    </div>
                                                    <div className="px-4 py-1.5 bg-emerald-50 rounded-full border border-emerald-100">
                                                        <span className="font-bold text-emerald-600 text-[18px]">₹{exp.amount}</span>
                                                    </div>
                                                </div>
                                                {exp.description && <p className="text-[14px] text-slate-600 leading-relaxed">{exp.description}</p>}
                                            </div>
                                            
                                            {exp.billImage && (
                                                <div 
                                                    className="w-32 h-32 rounded-2xl overflow-hidden border border-slate-200 cursor-pointer relative group flex-shrink-0"
                                                    onClick={() => setSelectedImage(exp.billImage)}
                                                >
                                                    <img src={`${API_URL}${exp.billImage}`} className="w-full h-full object-cover" alt="Bill Receipt" />
                                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/></svg>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ) : null}

                <div className="flex items-center gap-2.5 mt-6 px-2 text-[11px] text-slate-400 font-medium flex-shrink-0">
                    <Info size={14} className="text-slate-300 flex-shrink-0" />
                    <span>Real-time tracking and field updates are synchronized natively from the Verve Kitchens Mobile app.</span>
                </div>
            </div>
        </Modal>
        
        {/* Full Image Overlay */}
        {selectedImage && (
            <div 
                className="fixed inset-0 z-[10000] bg-black/95 flex items-center justify-center cursor-zoom-out p-4"
                onClick={() => setSelectedImage(null)}
            >
                <img 
                    src={`${API_URL}${selectedImage}`} 
                    alt="Full view" 
                    className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl" 
                />
                <button 
                   className="absolute top-6 right-6 text-white bg-white/10 p-2 rounded-full hover:bg-white/20 transition-colors cursor-pointer"
                   onClick={(e) => { e.stopPropagation(); setSelectedImage(null); }}
                >
                   <X size={24} />
                </button>
            </div>
        )}
        </>
    );
};
